/**
 * Detects whether an auth flow was started from Agent Grid, so the auth views can
 * render Agent Grid branding instead of Anima's.
 *
 * Agent Grid reaches /login through the public API's OAuth authorize endpoint, which
 * sets `platform=agentgrid` on the login URL. We also sniff the redirect target so the
 * branding survives links that carry the authorize URL but lost the `platform` param.
 */

export const AGENT_GRID_PLATFORM = 'agentgrid';

const AGENT_GRID_HOSTNAME = 'agentgrid.io';
const AGENT_GRID_PATH = '/auth/agentgrid/';
const AGENT_GRID_CLIENT_ID = 'agentgrid-web';
const URL_PARSE_BASE = 'https://animaapp.com';

const isAgentGridHostname = (hostname) => {
  const normalized = hostname?.toLowerCase();
  return normalized === AGENT_GRID_HOSTNAME || !!normalized?.endsWith(`.${AGENT_GRID_HOSTNAME}`);
};

const parseUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    // Relative redirects ("/auth/agentgrid/callback") still need a base to parse.
    return new URL(value, typeof window === 'undefined' ? URL_PARSE_BASE : window.location.origin);
  } catch (err) {
    return null;
  }
};

export const isAgentGridRedirect = (redirect) => {
  const url = parseUrl(redirect);
  if (!url) {
    return false;
  }

  if (isAgentGridHostname(url.hostname)) {
    return true;
  }

  if (url.pathname.toLowerCase().includes(AGENT_GRID_PATH)) {
    return true;
  }

  // The login redirect points at the public API authorize URL, which carries the
  // Agent Grid client id and the Agent Grid app callback as OAuth params.
  if (url.searchParams.get('client_id')?.toLowerCase() === AGENT_GRID_CLIENT_ID) {
    return true;
  }

  const redirectUri = parseUrl(url.searchParams.get('redirect_uri'));
  return !!redirectUri && isAgentGridHostname(redirectUri.hostname);
};

export const isAgentGridSource = (query) => {
  const platform = query?.platform;
  if (typeof platform === 'string' && platform.toLowerCase() === AGENT_GRID_PLATFORM) {
    return true;
  }

  return isAgentGridRedirect(query?.redirect);
};
